import React, {useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {FaRegCommentAlt,FaBold,FaItalic,FaLink} from 'react-icons/fa';
import Service from '../../services/YoungApprenticeInvoiceNotesService';
import {sanitizeInvoiceNotesHtml,invoiceNotesCharacterCount,isSafeInvoiceNotesUrl} from './invoiceNotesHtml';
import './InvoiceNotesButton.css';

export default function InvoiceNotesButton({canManage=false,onSaved}) {
  const [open,setOpen]=useState(false),[loading,setLoading]=useState(false),[loaded,setLoaded]=useState(false),[saving,setSaving]=useState(false);
  const [html,setHtml]=useState(''),[error,setError]=useState(''),[color,setColor]=useState('#17324d');
  const [linkOpen,setLinkOpen]=useState(false),[linkText,setLinkText]=useState(''),[linkUrl,setLinkUrl]=useState('');
  const editor=useRef(null),selection=useRef(null);
  const count=invoiceNotesCharacterCount(html),editable=canManage&&loaded&&!loading&&!saving;
  const show=async()=>{
    selection.current=null;
    setError('');setLoaded(false);setLoading(true);setOpen(true);setLinkOpen(false);
    try {
      const data=await Service.get(),value=sanitizeInvoiceNotesHtml(data?.html);
      setHtml(value);setLoaded(true);
      if(editor.current)editor.current.innerHTML=value;
    }catch{setError('Não foi possível carregar as observações. Tente novamente.');}
    finally{setLoading(false);}
  };
  const rememberSelection=()=>{
    const selected=window.getSelection();
    if(selected?.rangeCount&&editor.current?.contains(selected.anchorNode))selection.current=selected.getRangeAt(0).cloneRange();
  };
  const restoreSelection=()=>{
    editor.current?.focus();
    if(selection.current&&!editor.current?.contains(selection.current.commonAncestorContainer))selection.current=null;
    if(selection.current){const selected=window.getSelection();selected.removeAllRanges();selected.addRange(selection.current);}
  };
  const changed=()=>{setHtml(sanitizeInvoiceNotesHtml(editor.current?.innerHTML));rememberSelection();};
  const format=(command,value)=>{restoreSelection();document.execCommand(command,false,value);changed();};
  const showLink=()=>{rememberSelection();setLinkText(selection.current?.toString()||'');setLinkUrl('');setLinkOpen(true);};
  const applyLink=()=>{
    if(!isSafeInvoiceNotesUrl(linkUrl.trim())||!linkText.trim())return;
    restoreSelection();
    const range=selection.current||document.createRange();
    if(!selection.current){range.selectNodeContents(editor.current);range.collapse(false);}
    const anchor=document.createElement('a');anchor.href=linkUrl.trim();
    if(range.toString()===linkText)anchor.append(range.extractContents());
    else {range.deleteContents();anchor.textContent=linkText;}
    range.insertNode(anchor);range.setStartAfter(anchor);range.collapse(true);selection.current=range;
    changed();setLinkOpen(false);
  };
  const paste=event=>{
    event.preventDefault();
    const rich=event.clipboardData.getData('text/html');
    const plain=event.clipboardData.getData('text/plain');
    const escaped=document.createElement('div');escaped.textContent=plain;
    document.execCommand('insertHTML',false,sanitizeInvoiceNotesHtml(rich||escaped.innerHTML.replace(/\n/g,'<br>')));changed();
  };
  const save=async()=>{
    if(!editable||count>400)return;
    setSaving(true);setError('');
    try{await Service.save({html:sanitizeInvoiceNotesHtml(html)});setOpen(false);onSaved?.();}
    catch(err){setError(err?.response?.data?.error||'Não foi possível salvar as observações.');}
    finally{setSaving(false);}
  };
  return <><button type="button" className="ya-compact-btn" onClick={show}><FaRegCommentAlt/> Observações</button>{open&&createPortal(
    <div className="ya-payment-info-screen" style={{zIndex:30000}} role="dialog" aria-modal="true" aria-label="Observações da Nota Fatura"><div className="ya-payment-info-shell">
      <header><h2>Observações da Nota Fatura</h2><button type="button" className="ya-compact-btn" disabled={saving} onClick={()=>setOpen(false)}>Fechar</button></header>
      <p>Este texto aparece no rodapé das próximas Notas Faturas. As faturas já emitidas mantêm suas observações originais.</p>
      {error&&<p role="alert">{error}</p>}{loading&&<p role="status">Carregando observações...</p>}
      <fieldset disabled={!editable} className="ya-invoice-notes-tools"><legend>Formatação</legend>
        <button type="button" aria-label="Negrito" onMouseDown={event=>event.preventDefault()} onClick={()=>format('bold')}><FaBold/></button>
        <button type="button" aria-label="Itálico" onMouseDown={event=>event.preventDefault()} onClick={()=>format('italic')}><FaItalic/></button>
        <label>Cor do texto <input type="color" aria-label="Cor do texto" value={color} onChange={event=>{setColor(event.target.value);format('foreColor',event.target.value);}}/></label>
        <button type="button" onMouseDown={event=>event.preventDefault()} onClick={showLink}><FaLink/> Inserir link</button>
      </fieldset>
      {linkOpen&&<fieldset disabled={!editable} className="ya-invoice-notes-link"><legend>Link no texto</legend>
        <label>Texto do link<input value={linkText} onChange={event=>setLinkText(event.target.value)}/></label>
        <label>Endereço do link<input type="url" placeholder="https://exemplo.com.br" value={linkUrl} onChange={event=>setLinkUrl(event.target.value)}/></label>
        <small>Use um endereço com https://, http:// ou mailto:.</small>
        <button type="button" disabled={!linkText.trim()||!isSafeInvoiceNotesUrl(linkUrl.trim())} onClick={applyLink}>Aplicar link</button>
        <button type="button" onClick={()=>setLinkOpen(false)}>Cancelar link</button>
      </fieldset>}
      <div ref={editor} className="ya-invoice-notes-editor" role="textbox" aria-label="Observações no rodapé" aria-multiline="true" aria-describedby="ya-invoice-notes-count" contentEditable={editable} suppressContentEditableWarning onInput={changed} onMouseUp={rememberSelection} onKeyUp={rememberSelection} onPaste={paste}/>
      <p id="ya-invoice-notes-count" aria-live="polite" className={count>400?'ya-invoice-notes-limit':''}>{count}/400 caracteres</p>
      {count>400&&<p role="alert">Reduza o texto para no máximo 400 caracteres visíveis.</p>}
      <footer><button type="button" className="ya-compact-btn" disabled={saving} onClick={()=>setOpen(false)}>Cancelar</button>{canManage&&<button type="button" className="ya-compact-btn ya-compact-btn--primary" disabled={!editable||count>400} onClick={save}>{saving?'Salvando...':'Salvar observações'}</button>}</footer>
    </div></div>,document.body)}</>;
}
